import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TextField,
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import axios from 'axios';
import { getAuthToken } from '../utils/authToken';

const PAGE_SIZE_OPTIONS = [5, 10, 25];

function AdminUsersTable() {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const tokenFromState = useSelector((state) => state.auth.token);
    const token = getAuthToken(tokenFromState);

    // Selected user state (by id)
    const [selectedUserId, setSelectedUserId] = useState(null);

    // Edit dialog state
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState({});

    // Reset-2FA dialog state
    const [reset2faUser, setReset2faUser] = useState(null);
    const [reset2faCode, setReset2faCode] = useState('');
    const [reset2faReason, setReset2faReason] = useState('');
    const [reset2faAllowAdmin, setReset2faAllowAdmin] = useState(false);
    const [reset2faSubmitting, setReset2faSubmitting] = useState(false);
    const [reset2faError, setReset2faError] = useState('');
    const [reset2faSuccess, setReset2faSuccess] = useState('');

    // Fetch all users on mount
    useEffect(() => {
        if (!token) {
            setUsers([]);
            return;
        }
        axios
            .get('/api/users', {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => setUsers(res.data))
            .catch(() => setUsers([]));
    }, [token]);

    // Filter users by search
    const filteredUsers = users.filter((user) =>
        [user.username, user.email, user.firstName, user.lastName]
            .join(' ')
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    // Pagination logic
    const pagedUsers = filteredUsers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Handle row checkbox
    const handleSelectUser = (userId) => {
        setSelectedUserId(selectedUserId === userId ? null : userId);
    };

    // ---- Edit Handlers ----
    const handleEditUser = () => {
        const user = users.find((u) => u._id === selectedUserId);
        if (!user) return;

        setEditUser(user);
        setEditForm({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            company: user.company || '',
            occupation: user.occupation || '',
            availability: user.availability || '',
            isAdmin: !!user.isAdmin,
            isVerified: !!user.isVerified,
            accountType: user.accountType || 'free',
        });
    };

    const handleEditFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm((f) => ({
            ...f,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSaveEdit = () => {
        const authToken = getAuthToken(token);
        if (!authToken) {
            window.alert('Authentication token missing. Please sign in again.');
            return;
        }
        axios
            .put(`/api/users/${editUser._id}`, editForm, {
                headers: { Authorization: `Bearer ${authToken}` },
            })
            .then((res) => {
                setUsers((users) =>
                    users.map((u) =>
                        u._id === editUser._id ? res.data.user : u
                    )
                );
                setEditUser(null);
                setSelectedUserId(null);
            });
    };

    const handleDeleteUser = () => {
        if (!selectedUserId) return;
        if (!window.confirm('Are you sure you want to delete this user?')) return;

        const authToken = getAuthToken(token);
        if (!authToken) {
            window.alert('Authentication token missing. Please sign in again.');
            return;
        }

        axios
            .delete(`/api/users/${selectedUserId}`, {
                headers: { Authorization: `Bearer ${authToken}` },
            })
            .then(() => {
                setUsers((users) =>
                    users.filter((u) => u._id !== selectedUserId)
                );
                setSelectedUserId(null);
            });
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                All Users
            </Typography>

            <Paper sx={{ mb: 2, p: 2, display: 'flex', alignItems: 'center' }}>
                <TextField
                    label="Search users"
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 300 }}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0); // reset page when searching
                    }}
                />
            </Paper>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell>Username</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>First Name</TableCell>
                            <TableCell>Last Name</TableCell>
                            <TableCell>Company</TableCell>
                            <TableCell>Occupation</TableCell>
                            <TableCell>Availability</TableCell>
                            <TableCell>Account Type</TableCell>
                            <TableCell>Admin</TableCell>
                            <TableCell>Verified</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {pagedUsers.map((u) => (
                            <TableRow
                                key={u._id}
                                hover
                                selected={u._id === selectedUserId}
                                onClick={() => handleSelectUser(u._id)}
                                style={{ cursor: 'pointer' }}
                            >
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={u._id === selectedUserId}
                                        onChange={() => handleSelectUser(u._id)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </TableCell>
                                <TableCell>{u.username}</TableCell>
                                <TableCell>{u.email}</TableCell>
                                <TableCell>{u.firstName}</TableCell>
                                <TableCell>{u.lastName}</TableCell>
                                <TableCell>{u.company}</TableCell>
                                <TableCell>{u.occupation}</TableCell>
                                <TableCell>{u.availability}</TableCell>
                                <TableCell>{u.accountType}</TableCell>
                                <TableCell>{u.isAdmin ? 'Yes' : 'No'}</TableCell>
                                <TableCell>{u.isVerified ? 'Yes' : 'No'}</TableCell>
                            </TableRow>
                        ))}
                        {pagedUsers.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={11} align="center">
                                    No users found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={filteredUsers.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            />

            {/* Edit & Delete buttons */}
            {selectedUserId && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleEditUser}
                        color="primary"
                    >
                        Edit Selected
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleDeleteUser}
                        color="error"
                    >
                        Delete Selected
                    </Button>
                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => {
                            const u = users.find((x) => x._id === selectedUserId);
                            if (!u) return;
                            setReset2faUser(u);
                            setReset2faCode('');
                            setReset2faReason('');
                            setReset2faAllowAdmin(false);
                            setReset2faError('');
                            setReset2faSuccess('');
                        }}
                    >
                        Reset 2FA
                    </Button>
                </Box>
            )}

            {/* --- Edit Dialog --- */}
            <Dialog open={!!editUser} onClose={() => setEditUser(null)}>
                <DialogTitle>Edit User</DialogTitle>
                <DialogContent>
                    <TextField
                        label="First Name"
                        name="firstName"
                        value={editForm.firstName}
                        onChange={handleEditFormChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Last Name"
                        name="lastName"
                        value={editForm.lastName}
                        onChange={handleEditFormChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Company"
                        name="company"
                        value={editForm.company}
                        onChange={handleEditFormChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Occupation"
                        name="occupation"
                        value={editForm.occupation}
                        onChange={handleEditFormChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Availability"
                        name="availability"
                        value={editForm.availability}
                        onChange={handleEditFormChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    {/* Account Type Dropdown */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Account Type</InputLabel>
                        <Select
                            name="accountType"
                            value={editForm.accountType || ''}
                            onChange={handleEditFormChange}
                            label="Account Type"
                        >
                            
                            <MenuItem value="free">Free</MenuItem>
                            <MenuItem value="essential">Essential</MenuItem>
                            <MenuItem value="growth">Growth</MenuItem>
                            <MenuItem value="scale">Scale</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Admin & Verified Checkboxes */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <label>
                            <input
                                type="checkbox"
                                name="isAdmin"
                                checked={!!editForm.isAdmin}
                                onChange={handleEditFormChange}
                                style={{ marginRight: 4 }}
                            />
                            Admin
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                name="isVerified"
                                checked={!!editForm.isVerified}
                                onChange={handleEditFormChange}
                                style={{ marginRight: 4 }}
                            />
                            Verified
                        </label>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditUser(null)}>Cancel</Button>
                    <Button onClick={handleSaveEdit} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset 2FA — destructive, audited, requires admin's own TOTP */}
            <Dialog
                open={!!reset2faUser}
                onClose={() => !reset2faSubmitting && setReset2faUser(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Reset 2FA for {reset2faUser?.email}</DialogTitle>
                <DialogContent>
                    {reset2faSuccess ? (
                        <Typography color="success.main">{reset2faSuccess}</Typography>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                This clears the user's authenticator secret and unused backup
                                codes, drops them into a fresh 7-day enrolment window, and
                                emails them about the reset. The action is logged with your
                                admin id, reason, and IP.
                            </Typography>
                            {reset2faError && (
                                <Typography color="error" sx={{ mb: 2 }}>{reset2faError}</Typography>
                            )}
                            <TextField
                                fullWidth
                                label="Your current 2FA code"
                                value={reset2faCode}
                                onChange={(e) => setReset2faCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                inputProps={{ inputMode: 'numeric', pattern: '\\d{6}' }}
                                placeholder="123456"
                                autoFocus
                                sx={{ mb: 2 }}
                                helperText="Confirms it's really you, not just your session."
                            />
                            <TextField
                                fullWidth
                                label="Reason (logged)"
                                value={reset2faReason}
                                onChange={(e) => setReset2faReason(e.target.value)}
                                multiline
                                minRows={2}
                                sx={{ mb: 2 }}
                                helperText="E.g. 'User confirmed lost phone over support call #4821.'"
                            />
                            {reset2faUser?.isAdmin && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Checkbox
                                        checked={reset2faAllowAdmin}
                                        onChange={(e) => setReset2faAllowAdmin(e.target.checked)}
                                    />
                                    <Typography variant="body2">
                                        I'm resetting 2FA on another admin — paired review approved.
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setReset2faUser(null)}
                        disabled={reset2faSubmitting}
                    >
                        {reset2faSuccess ? 'Close' : 'Cancel'}
                    </Button>
                    {!reset2faSuccess && (
                        <Button
                            color="warning"
                            variant="contained"
                            disabled={
                                reset2faSubmitting ||
                                reset2faCode.length !== 6 ||
                                reset2faReason.trim().length < 4 ||
                                (reset2faUser?.isAdmin && !reset2faAllowAdmin)
                            }
                            onClick={async () => {
                                setReset2faSubmitting(true);
                                setReset2faError('');
                                try {
                                    await axios.post(
                                        `/api/admin/2fa/users/${reset2faUser._id}/reset`,
                                        {
                                            code: reset2faCode,
                                            reason: reset2faReason.trim(),
                                            allowAdminTarget: reset2faAllowAdmin,
                                        },
                                        { headers: { Authorization: `Bearer ${token}` } },
                                    );
                                    setReset2faSuccess(
                                        `2FA reset. ${reset2faUser.email} has been notified by email.`,
                                    );
                                } catch (err) {
                                    setReset2faError(
                                        err.response?.data?.msg || 'Reset failed.',
                                    );
                                } finally {
                                    setReset2faSubmitting(false);
                                }
                            }}
                        >
                            {reset2faSubmitting ? 'Resetting…' : 'Reset 2FA'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminUsersTable;
